import { FC, useContext, useEffect, useState } from 'react';

import { Link } from '@mui/material';
import {
  GridCallbackDetails,
  GridColDef,
  GridFilterModel,
  GridPaginationModel,
  GridRowsProp,
  GridSortModel,
} from '@mui/x-data-grid';

import {
  FuturesUserRole,
  GrainTraderRole,
  GranelerasTraderRole,
  MillingTraderRole,
  SuperUserRole,
} from '../../../../../app/UserWrapper/UserWrapper';
import { ChangeableGrid } from '../../../../../components';
import {
  dateValueFormatter,
  errorCellClassName,
  numberWithComma5DecimalsFormatter,
  yesNoGridColTypeDef,
} from '../../../../../components/gridUtils';
import { UserContext } from '../../../../../Context';
import {
  useFuturesExchangesViewQuery,
  useFuturesUnitOfMeasuresViewQuery,
} from '../../../../../hooks';

export interface FuturesContractsGridProps {
  userName: string;
  listQuery: any;
  paginationModel?: GridPaginationModel;
  setPaginationModel?: (model: GridPaginationModel, details: GridCallbackDetails) => void;
  onSortModelChange?: (model: GridSortModel, details: GridCallbackDetails) => void;
  onFilterModelChange?: (model: GridFilterModel, details: GridCallbackDetails<'filter'>) => void;
  onNewButtonClick: () => void;
  onEditButtonClick: (id: string) => void;
}

const FuturesContractsGrid: FC<FuturesContractsGridProps> = ({
  // userName,
  listQuery,
  paginationModel,
  setPaginationModel,
  onSortModelChange,
  onFilterModelChange,
  onNewButtonClick,
  onEditButtonClick,
}) => {
  let rows: GridRowsProp[] = [];
  if (listQuery.data?.result?.entries) {
    rows = listQuery.data?.result.entries.map((t: any) => {
      return {
        ...t,
        id: t.futuresContractId,
      } as unknown as GridRowsProp;
    });
  }
  const rowCount = listQuery.data?.result?.totalCount;
  const [rowCountState, setRowCountState] = useState(rowCount || 0);
  useEffect(() => {
    setRowCountState((prevRowCountState: any) =>
      rowCount !== undefined ? rowCount : prevRowCountState
    );
  }, [rowCount, setRowCountState]);

  const userContext = useContext(UserContext);
  const hasEnterFuturesContractsPermission = userContext.haveRole([
    GrainTraderRole,
    GranelerasTraderRole,
    MillingTraderRole,
    FuturesUserRole,
    SuperUserRole,
  ]);
  useFuturesExchangesViewQuery();
  useFuturesUnitOfMeasuresViewQuery();
  const columns: GridColDef[] = [
    {
      field: 'futuresContractCode',
      headerName: 'Contract Code',
      type: 'string',
      flex: 1,
      editable: false,
      cellClassName: errorCellClassName,
      renderCell: ({ value, row }) => {
        return hasEnterFuturesContractsPermission ? (
          <Link
            onClick={() => onEditButtonClick(row.id)}
            sx={{ cursor: 'pointer' }}
            data-testid="LinkToModal"
          >
            {value || '______'}
          </Link>
        ) : (
          <>{value || '______'}</>
        );
      },
    },
    {
      field: 'futuresContractName',
      headerName: 'Contract Name',
      type: 'string',
      flex: 2,
      editable: false,
      cellClassName: errorCellClassName,
    },
    {
      field: 'exchangeName',
      headerName: 'Exchange',
      type: 'string',
      flex: 2,
      editable: false,
      cellClassName: errorCellClassName,
    },
    {
      field: 'contractMonths',
      headerName: 'Months Traded',
      type: 'string',
      flex: 2,
      editable: false,
      cellClassName: errorCellClassName,
    },
    {
      field: 'contractUnits',
      headerName: 'Units per Contract',
      type: 'string',
      flex: 1,
      editable: false,
      cellClassName: errorCellClassName,
    },
    {
      field: 'uomPluralName',
      headerName: 'Unit of Measure',
      type: 'string',
      flex: 2,
      editable: false,
      cellClassName: errorCellClassName,
    },
    {
      field: 'closePriceConversionFactor',
      headerName: 'Close Price Conversion Factor',
      type: 'string',
      flex: 1,
      editable: false,
      valueFormatter: numberWithComma5DecimalsFormatter,
      cellClassName: errorCellClassName,
    },
    {
      field: 'pricingUnitDescription',
      headerName: 'Pricing Unit Description',
      type: 'string',
      flex: 2,
      editable: false,
      cellClassName: errorCellClassName,
    },
    {
      field: 'isActive',
      headerName: 'Active',
      type: 'boolean',
      flex: 1,
      editable: false,
      ...yesNoGridColTypeDef('isActive'),
      cellClassName: errorCellClassName,
    },
    {
      field: 'updatedOn',
      headerName: 'Updated On',
      type: 'string',
      flex: 2,
      editable: false,
      valueFormatter: dateValueFormatter,
      cellClassName: errorCellClassName,
    },
    {
      field: 'updatedBy',
      headerName: 'Updated By',
      type: 'string',
      flex: 2,
      editable: false,
      cellClassName: errorCellClassName,
    },
  ];

  return (
    <div data-testid="FuturesContractsGrid">
      <ChangeableGrid
        initialRows={rows}
        isLoading={listQuery.isPending}
        columns={columns}
        onAdd={hasEnterFuturesContractsPermission ? onNewButtonClick : undefined}
        rowCount={rowCountState}
        pageSizeOptions={[25, 50, 100]}
        paginationModel={paginationModel}
        paginationMode="server"
        sortingMode="server"
        filterMode="server"
        onPaginationModelChange={setPaginationModel}
        onSortModelChange={onSortModelChange}
        onFilterModelChange={onFilterModelChange}
        disableColumnFilter
        disableColumnMenu
        disableColumnSelector
        disableDensitySelector
        onProcessRowUpdateError={console.error}
        initialState={{
          sorting: {
            sortModel: [{ field: 'futuresContractId', sort: 'desc' }],
          },
          pagination: { paginationModel: { pageSize: 50 } },
        }}
      />
    </div>
  );
};

export default FuturesContractsGrid;
